/**
 * WaImageAlbum — render de álbum de imágenes/videos al estilo WhatsApp Web.
 *
 * Cuando el contacto (o nosotros) envía múltiples imágenes en sucesión
 * rápida, WhatsApp Web las agrupa en una sola "burbuja" con grid:
 *   - 2 imgs → 2 columnas, 1 fila
 *   - 3 imgs → 1 ancha arriba + 2 abajo
 *   - 4 imgs → grid 2×2
 *   - 5+ imgs → grid 2×2 con overlay "+N" en el 4to tile
 *
 * Click en cualquier tile abre el lightbox con esa imagen específica.
 * Click en el "+N" abre la 4ta imagen (el lightbox por ahora no tiene
 * navegación; ver tile-by-tile sigue funcionando).
 */

import type { WaMessage } from './WaMessageThread';

const ALBUM_WIDTH = 290;

interface Props {
  msgs: WaMessage[];
  isOutbound: boolean;
  isNote: boolean;
  /**
   * Abre el lightbox con TODAS las imágenes del álbum en modo carrusel,
   * arrancando en `startIndex`. Click en tile N ⇒ abre con N como índice
   * inicial; click en overlay "+N" ⇒ abre en el índice 3 (4to tile) y el
   * usuario navega con flechas/strip.
   */
  onLightboxOpen: (msgs: WaMessage[], startIndex: number) => void;
}

export function WaImageAlbum({ msgs, isOutbound, isNote, onLightboxOpen }: Props) {
  const count = msgs.length;
  const visible = msgs.slice(0, 4);
  const overflow = count > 4 ? count - 4 : 0;

  // Layout: aspect-ratio + grid-template del contenedor depende del count.
  // Hacemos los gaps transparentes (border) en lugar de margin para no
  // afectar el aspect-ratio.
  const gridStyle: React.CSSProperties = (() => {
    if (count === 2) {
      return {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr',
        aspectRatio: '2 / 1',
      };
    }
    if (count === 3) {
      return {
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        aspectRatio: '1 / 1',
      };
    }
    // 4+ → 2×2
    return {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      aspectRatio: '1 / 1',
    };
  })();

  // Background bubble: hereda el estilo de la burbuja del row.
  // Lo dejamos sobrio porque el grid es lo dominante.
  const bubbleBg = isNote
    ? 'var(--p-color-bg-surface-warning)'
    : isOutbound
    ? '#dcf8c6'
    : 'var(--p-color-bg-surface)';

  // Caption: WhatsApp solo permite caption en el PRIMER item del álbum.
  const caption = msgs[0]?.media_caption || msgs[0]?.body || '';
  // Timestamp + status: del último mensaje del álbum (más reciente).
  const last = msgs[msgs.length - 1];
  const timestamp = new Date(last.created_at).toLocaleTimeString('es-PE', {
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div style={{
      maxWidth: ALBUM_WIDTH,
      borderRadius: 8,
      overflow: 'hidden',
      background: bubbleBg,
      boxShadow: '0 1px 0.5px rgba(11,20,26,0.13)',
    }}>
      <div style={{
        display: 'grid',
        gap: 2,
        ...gridStyle,
        background: 'transparent',
      }}>
        {visible.map((m, idx) => {
          const url = m.media_url ?? m.media_thumbnail ?? '';
          const isThumb = !m.media_url && !!m.media_thumbnail;
          const showOverflow = idx === 3 && overflow > 0;
          // Tile 0 en layout de 3 ocupa fila completa (grande arriba).
          const span3Top = count === 3 && idx === 0
            ? { gridColumn: '1 / -1' }
            : {};
          return (
            <div
              key={m.id}
              onClick={() => onLightboxOpen(msgs, idx)}
              style={{
                position: 'relative',
                cursor: url ? 'pointer' : 'default',
                background: '#0001',
                overflow: 'hidden',
                ...span3Top,
              }}
            >
              {url ? (
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    filter: isThumb ? 'blur(6px)' : 'none',
                    transition: 'filter 0.3s ease-out',
                  }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--p-color-text-subdued)', fontSize: 12,
                }}>
                  …
                </div>
              )}
              {showOverflow ? (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(0,0,0,0.55)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 36, fontWeight: 500,
                  letterSpacing: -0.5,
                }}>
                  +{overflow}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Caption + timestamp. Estilo WhatsApp Web: caption arriba, timestamp
          abajo a la derecha. Si NO hay caption, solo timestamp. */}
      {caption ? (
        <div style={{
          padding: '6px 9px 8px',
          fontSize: 14,
          color: 'var(--p-color-text)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}>
          {caption}
          <div style={{
            display: 'flex', justifyContent: 'flex-end',
            fontSize: 11, color: 'var(--p-color-text-subdued)',
            marginTop: 2,
          }}>
            {timestamp}
          </div>
        </div>
      ) : (
        <div style={{
          padding: '4px 9px 6px',
          display: 'flex', justifyContent: 'flex-end',
          fontSize: 11, color: 'var(--p-color-text-subdued)',
        }}>
          {timestamp}
        </div>
      )}
    </div>
  );
}

// ─── Helper de agrupamiento ───
//
// WhatsApp envía albums como mensajes individuales con timestamps muy
// cercanos. Heurística: agrupar mensajes consecutivos del mismo sender,
// del mismo tipo (image/video), sin reply, sin reaction, dentro de 30s.

const ALBUM_WINDOW_MS = 30_000;

export function isAlbumCandidate(msg: WaMessage): boolean {
  if (msg.msg_type !== 'image' && msg.msg_type !== 'video') return false;
  if (msg.is_internal_note) return false;
  if (msg.quoted_message_id) return false;
  return true;
}

export function canExtendAlbum(prev: WaMessage, next: WaMessage): boolean {
  if (!isAlbumCandidate(prev) || !isAlbumCandidate(next)) return false;
  if (prev.direction !== next.direction) return false;
  if (prev.sender_type !== next.sender_type) return false;
  if (prev.sender_id !== next.sender_id) return false;
  // Caption solo en primero — si el siguiente trae caption, NO es álbum.
  if (next.media_caption || next.body) return false;
  const dt = new Date(next.created_at).getTime() - new Date(prev.created_at).getTime();
  return dt >= 0 && dt <= ALBUM_WINDOW_MS;
}
